import {
  BulbOutlined,
  CheckCircleFilled,
  DownloadOutlined,
  FileSearchOutlined,
  ReloadOutlined,
  RobotOutlined,
  SafetyCertificateOutlined,
  SolutionOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { Alert, Button, Spin, Tag, Tooltip } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { CandidateAiSummaryView } from '../models/candidate-ai-summary.model';
import { CandidateAiSummaryService } from '../services/candidate-ai-summary.service';
import {
  candidateSummarySkillLevelColor,
  sortCandidateSummarySkills,
} from '../utils/candidate-ai-summary.utils';
import './CandidateAiSummary.css';

interface CandidateAiSummaryProps {
  candidateId: string;
  candidateName: string;
  refreshToken?: number;
  hasUnsavedProfileChanges?: boolean;
}

const formatDate = (value?: string) => {
  if (!value) return '';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
};

const filenamePart = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '') || 'candidate';

export const candidateSummaryPdfFilename = (candidateName: string) =>
  `candidate-summary-${filenamePart(candidateName)}.pdf`;

const recruiterInsight = (value: string) => {
  const match = value.match(
    /^(Strength|Application signal|Screening question|Evidence gap)\s*(?:—|-|:)\s*(.+)$/i
  );
  if (!match) return { label: 'Recruiter note', body: value, tone: 'note' };
  const label = match[1].replace(/^./, (character) => character.toUpperCase());
  return {
    label,
    body: match[2],
    tone: label.toLowerCase().replace(/\s+/g, '-'),
  };
};

const CandidateAiSummary = ({
  candidateId,
  candidateName,
  refreshToken = 0,
  hasUnsavedProfileChanges = false,
}: CandidateAiSummaryProps) => {
  const [summary, setSummary] = useState<CandidateAiSummaryView | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const existing = await CandidateAiSummaryService.Get(
        candidateId,
        (requestError) => {
          if (requestError.message === 'NOT FOUND') return;
          throw requestError;
        }
      );
      setSummary(existing?.payload ? existing : null);
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : 'Could not load the candidate summary.'
      );
    } finally {
      setLoading(false);
    }
  }, [candidateId]);

  useEffect(() => {
    void load();
  }, [load, refreshToken]);

  const generate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const generated = await CandidateAiSummaryService.Generate(candidateId);
      setSummary(generated);
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : 'Candidate summary generation failed.'
      );
    } finally {
      setGenerating(false);
    }
  };

  const download = async () => {
    setDownloading(true);
    setError(null);
    try {
      const blob = await CandidateAiSummaryService.DownloadPdf(candidateId);
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = candidateSummaryPdfFilename(candidateName);
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : 'Could not download the PDF.'
      );
    } finally {
      setDownloading(false);
    }
  };

  const regenerateDisabled =
    generating ||
    hasUnsavedProfileChanges ||
    Boolean(summary && !summary.canRegenerate);
  const regenerateHint = hasUnsavedProfileChanges
    ? 'Save the profile changes before generating a new summary.'
    : summary && !summary.canRegenerate
      ? 'Regeneration becomes available when candidate details change.'
      : undefined;
  const sortedTopSkills = sortCandidateSummarySkills(
    summary?.payload.topSkills
  );
  const sourceSnapshot = summary?.sourceSnapshot;
  const overview = sourceSnapshot
    ? [
        {
          label: 'Latest CV',
          value: sourceSnapshot.latestResume?.name || 'Not available',
          detail: sourceSnapshot.latestResume?.uploadedAt
            ? `Uploaded ${formatDate(sourceSnapshot.latestResume.uploadedAt)}`
            : 'No saved resume found',
          icon: <FileSearchOutlined />,
        },
        {
          label: 'Latest process',
          value: sourceSnapshot.latestProcess?.jobName || 'Not recorded',
          detail: sourceSnapshot.latestProcess
            ? [
                sourceSnapshot.latestProcess.clientName,
                sourceSnapshot.latestProcess.status,
                sourceSnapshot.latestProcess.closed ? 'Closed' : 'Open',
              ]
                .filter(Boolean)
                .join(' · ')
            : 'No recruiting process found',
          icon: <SolutionOutlined />,
        },
        {
          label: 'Recruiter rating',
          value:
            sourceSnapshot.recruiterRating === null
              ? 'Not rated'
              : `${sourceSnapshot.recruiterRating} / 5`,
          detail: 'Manual recruiter assessment',
          icon: <BulbOutlined />,
        },
        {
          label: 'ATS score',
          value:
            sourceSnapshot.atsScore === null
              ? 'Not calculated'
              : `${Math.round(sourceSnapshot.atsScore)} / 100`,
          detail: 'Calculated for the latest process',
          icon: <ThunderboltOutlined />,
        },
      ]
    : summary
      ? [
          {
            label: 'Evidence-backed skills',
            value: sortedTopSkills.length,
            detail: 'From saved candidate evidence',
            icon: <SafetyCertificateOutlined />,
          },
          {
            label: 'Experience signals',
            value: summary.payload.experienceHighlights?.length || 0,
            detail: 'Highlights available for review',
            icon: <ThunderboltOutlined />,
          },
          {
            label: 'Suitable positions',
            value: summary.payload.suitableRoles?.length || 0,
            detail: 'Role directions supported',
            icon: <SolutionOutlined />,
          },
          {
            label: 'Recruiter prompts',
            value: summary.payload.recruiterInsights?.length || 0,
            detail: 'Items to discuss or verify',
            icon: <BulbOutlined />,
          },
        ]
      : [];
  const hasCredentials = Boolean(
    summary?.payload.notableAchievements?.length ||
      summary?.payload.certifications?.length
  );

  return (
    <section
      className="candidate-ai-summary"
      aria-labelledby="candidate-ai-title"
    >
      <header className="candidate-ai-summary__header">
        <div className="candidate-ai-summary__identity">
          <span>
            <RobotOutlined aria-hidden />
          </span>
          <div>
            <div>
              <h2 id="candidate-ai-title">AI Candidate Summary</h2>
              {summary ? (
                <Tag
                  color={summary.stale ? 'gold' : 'success'}
                  icon={
                    summary.stale ? <ReloadOutlined /> : <CheckCircleFilled />
                  }
                >
                  {summary.stale ? 'Needs refresh' : 'Up to date'}
                </Tag>
              ) : null}
            </div>
            <p>
              The candidate's current state, grounded in the latest resume,
              recruiting activity, recruiter feedback, and ATS evidence.
            </p>
          </div>
        </div>
        <div className="candidate-ai-summary__actions">
          {summary ? (
            <Button
              icon={<DownloadOutlined />}
              loading={downloading}
              onClick={() => void download()}
            >
              Download PDF
            </Button>
          ) : null}
          <Tooltip title={regenerateHint}>
            <span>
              <Button
                type={summary ? 'default' : 'primary'}
                icon={summary ? <ReloadOutlined /> : <RobotOutlined />}
                loading={generating}
                disabled={regenerateDisabled}
                onClick={() => void generate()}
              >
                {summary
                  ? summary.canRegenerate
                    ? 'Regenerate summary'
                    : 'Summary up to date'
                  : 'Generate summary'}
              </Button>
            </span>
          </Tooltip>
        </div>
      </header>

      {hasUnsavedProfileChanges ? (
        <Alert
          type="info"
          showIcon
          message="Save profile changes first"
          description="The summary uses saved candidate information, skills, files, and applications."
        />
      ) : null}

      {summary?.stale ? (
        <Alert
          type="warning"
          showIcon
          message="Candidate information changed"
          description="Regenerate the summary before sharing it so the PDF reflects the latest details."
          action={
            <Button
              size="small"
              type="primary"
              loading={generating}
              onClick={() => void generate()}
            >
              Regenerate
            </Button>
          }
        />
      ) : null}

      {error ? (
        <Alert
          type="error"
          showIcon
          message="AI Candidate Summary unavailable"
          description={error}
          action={
            <Button size="small" onClick={() => void load()}>
              Retry
            </Button>
          }
        />
      ) : null}

      {loading ? (
        <div className="candidate-ai-summary__state" role="status">
          <Spin />
          <div>
            <strong>Loading candidate summary</strong>
            <span>Checking the latest saved version and source data.</span>
          </div>
        </div>
      ) : generating && !summary ? (
        <div className="candidate-ai-summary__state candidate-ai-summary__state--generating">
          <Spin />
          <div>
            <strong>Building the AI Candidate Summary</strong>
            <span>
              Reviewing resume evidence, skills, inferred roles, and application
              history.
            </span>
          </div>
        </div>
      ) : summary ? (
        <div className="candidate-ai-summary__content">
          <section
            className="candidate-ai-summary__overview"
            aria-label="Current candidate state"
          >
            {overview.map((item) => (
              <article key={item.label}>
                <span aria-hidden>{item.icon}</span>
                <div>
                  <p>{item.label}</p>
                  <strong title={String(item.value)}>{item.value}</strong>
                  <small>{item.detail}</small>
                </div>
              </article>
            ))}
          </section>

          {sourceSnapshot ? (
            <section className="candidate-ai-summary__section candidate-ai-summary__notes">
              <header>
                <span>
                  <FileSearchOutlined />
                </span>
                <div>
                  <h3>Latest recruiter annotations</h3>
                  <p>Notes captured during the candidate's latest process.</p>
                </div>
              </header>
              <div className="candidate-ai-summary__note-list">
                {sourceSnapshot.recruiterNotes.length ? (
                  sourceSnapshot.recruiterNotes.map((note, index) => (
                    <article key={`${note.createdAt || index}-${note.content}`}>
                      <p>{note.content}</p>
                      <span>
                        {note.author || 'Recruiter'}
                        {note.createdAt
                          ? ` · ${formatDate(note.createdAt)}`
                          : ''}
                      </span>
                    </article>
                  ))
                ) : (
                  <p className="candidate-ai-summary__empty">
                    No recruiter annotations were recorded for the latest
                    process.
                  </p>
                )}
              </div>
            </section>
          ) : null}

          <section className="candidate-ai-summary__hero">
            <span>Executive recruiter brief</span>
            <h3>{summary.payload.headline}</h3>
            <p>{summary.payload.professionalSummary}</p>
          </section>

          <section className="candidate-ai-summary__section">
            <header>
              <span>
                <SafetyCertificateOutlined />
              </span>
              <div>
                <h3>Top skills</h3>
                <p>Evidence summarized from the candidate record.</p>
              </div>
            </header>
            <div className="candidate-ai-summary__skills">
              {sortedTopSkills.length ? (
                sortedTopSkills.map((skill) => (
                  <article key={`${skill.skill}-${skill.level}`}>
                    <div>
                      <strong>{skill.skill}</strong>
                      <Tag color={candidateSummarySkillLevelColor(skill.level)}>
                        {skill.level}
                      </Tag>
                    </div>
                    <p>{skill.evidence}</p>
                  </article>
                ))
              ) : (
                <p className="candidate-ai-summary__empty">
                  No verified skills were available.
                </p>
              )}
            </div>
          </section>

          <div className="candidate-ai-summary__grid">
            <section className="candidate-ai-summary__section">
              <header>
                <span>
                  <SolutionOutlined />
                </span>
                <div>
                  <h3>Suitable positions</h3>
                  <p>Role families supported by the latest evidence.</p>
                </div>
              </header>
              <div className="candidate-ai-summary__roles">
                {summary.payload.suitableRoles?.length ? (
                  summary.payload.suitableRoles.map((role) => (
                    <Tag key={role}>{role}</Tag>
                  ))
                ) : (
                  <p className="candidate-ai-summary__empty">
                    No suitable positions established yet.
                  </p>
                )}
              </div>
            </section>

            <section className="candidate-ai-summary__section">
              <header>
                <span>
                  <ThunderboltOutlined />
                </span>
                <div>
                  <h3>Experience highlights</h3>
                  <p>Strong signals from the latest CV and ATS record.</p>
                </div>
              </header>
              <SummaryList
                values={summary.payload.experienceHighlights}
                numbered
              />
            </section>
          </div>

          {hasCredentials ? (
            <div className="candidate-ai-summary__grid candidate-ai-summary__credentials">
              <section className="candidate-ai-summary__section">
                <header>
                  <span>
                    <ThunderboltOutlined />
                  </span>
                  <div>
                    <h3>Notable achievements</h3>
                    <p>Outcomes explicitly supported by the latest CV.</p>
                  </div>
                </header>
                <SummaryList values={summary.payload.notableAchievements} />
              </section>

              <section className="candidate-ai-summary__section">
                <header>
                  <span>
                    <SafetyCertificateOutlined />
                  </span>
                  <div>
                    <h3>Certifications</h3>
                    <p>Credentials explicitly listed in the latest CV.</p>
                  </div>
                </header>
                <SummaryList values={summary.payload.certifications} />
              </section>
            </div>
          ) : null}

          <section className="candidate-ai-summary__section candidate-ai-summary__insights">
            <header>
              <span>
                <BulbOutlined />
              </span>
              <div>
                <h3>Recruiter insights</h3>
                <p>Items to confirm in screening or the next interview.</p>
              </div>
            </header>
            <RecruiterInsights values={summary.payload.recruiterInsights} />
          </section>

          <footer className="candidate-ai-summary__footer">
            <span>
              <FileSearchOutlined />
              Generated {formatDate(summary.updatedAt)}
              {summary.generatedBy ? ` by ${summary.generatedBy}` : ''}
            </span>
            <span>
              AI-generated content should be reviewed before making hiring
              decisions.
            </span>
          </footer>
        </div>
      ) : (
        <div className="candidate-ai-summary__empty-state">
          <span>
            <RobotOutlined />
          </span>
          <h3>Create a reusable candidate brief</h3>
          <p>
            Summarize the candidate once, download a polished PDF, and
            regenerate only when the underlying profile changes.
          </p>
          <div>
            <span>Latest CV</span>
            <span>Recruiter notes and rating</span>
            <span>ATS score</span>
            <span>Skills and suitable positions</span>
          </div>
          <Button
            type="primary"
            icon={<RobotOutlined />}
            disabled={hasUnsavedProfileChanges}
            loading={generating}
            onClick={() => void generate()}
          >
            Generate AI summary
          </Button>
        </div>
      )}
    </section>
  );
};

const SummaryList = ({
  values,
  numbered = false,
}: {
  values?: string[];
  numbered?: boolean;
}) =>
  values?.length ? (
    <ol
      className={`candidate-ai-summary__list${
        numbered ? ' candidate-ai-summary__list--numbered' : ''
      }`}
    >
      {values.map((value) => (
        <li key={value}>{value}</li>
      ))}
    </ol>
  ) : (
    <p className="candidate-ai-summary__empty">
      No evidence available for this section.
    </p>
  );

const RecruiterInsights = ({ values }: { values?: string[] }) =>
  values?.length ? (
    <ol className="candidate-ai-summary__insight-list">
      {values.map((value) => {
        const insight = recruiterInsight(value);
        return (
          <li key={value} data-tone={insight.tone}>
            <span>{insight.label}</span>
            <p>{insight.body}</p>
          </li>
        );
      })}
    </ol>
  ) : (
    <p className="candidate-ai-summary__empty">
      No recruiter insights available yet.
    </p>
  );

export default CandidateAiSummary;
